import { GetChapterRequest } from '@app/_proto/Protos/chapters';
import { ChaptersClient } from '@app/_proto/Protos/chapters.client';
import type { GetNovelResponse, NovelItem } from '@app/_proto/Protos/novels';
import { UnlockItemMethod } from '@app/_proto/Protos/unlocks';
import { logGAEvent } from '@app/analytics';
import { CHAPTER_EVENT_SOURCES, CHAPTER_UNLOCK_TYPES } from '@app/analytics/constants';
import { analyticsFactory } from '@app/analytics/handlers';
import useRouteFrom from '@app/analytics/hooks/useRouteFrom';
import NoLongerAvailable from '@app/components/NoLongerAvailable';
import NoLongerAvailableChapterLocked from '@app/components/NoLongerAvailableChapterLocked';
import { useChapterSettings } from '@app/components/chapters/hooks';
import ApiError from '@app/components/error.api';
import { useTheme } from '@app/components/hooks';
import PageHeading from '@app/components/page.heading';
import { ContentCenter } from '@app/components/shared/generic.styles';
import useChampionSubscriptions from '@app/domains/champion/hooks/useChampionSubscriptions';
import { ChapterEvents } from '@app/domains/chapter/analytics/posthog/events';
import ChapterContentPage from '@app/domains/chapter/components/ChapterContentPage';
import ChapterHeader from '@app/domains/chapter/components/ChapterHeader';
import ChapterLinks from '@app/domains/chapter/components/ChapterLinks';
import ChapterPlaceholder from '@app/domains/chapter/components/ChapterPlaceholder';
import Chapter from '@app/domains/chapter/containers/Chapter';
import { CurrentChapterContext } from '@app/domains/chapter/hooks/useCurrentChapter';
import NoSsr from '@app/domains/common/components/NoSsr';
import useChapterLockStatus from '@app/domains/common/hooks/useChapterLockStatus';
import useUserStatus from '@app/domains/common/hooks/useUserStatus';
import { parseChapterStatus } from '@app/domains/common/utils';
import { ToastMessageTypes, toastMessageFactory } from '@app/domains/common/utils/toast';
import useNovelVipBenefit from '@app/domains/novel/hooks/useNovelVipBenefit';
import { determineChapterStatus } from '@app/domains/novel/utils';
import { useGrpcApiWithQuery } from '@app/libs/api';
import { useAuth } from '@app/libs/auth';
import { GrpcError, useGrpcRequest } from '@app/libs/grpc';
import { timestampToUnix } from '@app/libs/utils';
import { Button } from '@mui/material';
import { GrpcStatusCode } from '@protobuf-ts/grpcweb-transport';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import produce from 'immer';
import { pick } from 'lodash-es';
import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';

type Props = {
    novel: NovelItem;
};

const ChapterNavigation = lazy(() => import('@app/domains/chapter/components/ChapterNavigation'), {
    ssr: false,
});

function ChapterPage({ novel }: Props) {
    const [navOpened, setNavOpened] = useState(false);

    const theme = useTheme();
    const { user } = useAuth();
    const { autoUnlockEnabled } = useUserStatus();
    const navigate = useNavigate();
    const { novelSlug, chapterSlug } = useParams();
    const queryClient = useQueryClient();
    const from = useRouteFrom();
    const [chapterSettings] = useChapterSettings();

    const { isVipBenefit: isVipBenefitNovel, isPending: isVipBenefitNovelPending } = useNovelVipBenefit(novel);

    const { activeSubscription, isPending: isChampionSubscriptionPending } = useChampionSubscriptions({
        novelId: novel?.id,
        enabled: !!novel?.id && !!novel.sponsorInfo?.hasAnyPlans?.value,
    });

    const getChapterRequest = useGrpcRequest(GetChapterRequest, {
        chapterProperty: {
            byProperty: {
                oneofKind: 'slugs',
                slugs: {
                    novelSlug: novelSlug!,
                    chapterSlug: chapterSlug!,
                },
            },
        },
    });

    //Server request to fetch chapter data based on novel & chapter slug
    const {
        data,
        refetch: loadChapter,
        error,
    } = useGrpcApiWithQuery(
        ChaptersClient,
        c => c.getChapter,
        getChapterRequest,
        ['chapter', novelSlug, chapterSlug, user?.id],
        {
            enabled: !!novelSlug && !!chapterSlug,
            suspense: true,
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            cacheTime: 1000 * 60 * 2, //Data cached for 2 minutes
            staleTime: Number.POSITIVE_INFINITY,
            async onSuccess({ item: chapter }) {
                queryClient.setQueryData(['novel', novelSlug], (updater: GetNovelResponse | undefined) =>
                    produce(updater, draft => {
                        if (draft?.item && chapter?.novelInfo?.series) {
                            draft.item.series = chapter.novelInfo.series;
                        }
                    })
                );

                const { isAdvancedChapter } = parseChapterStatus(chapter);
                const { unlockInfo } = chapter?.relatedUserInfo || {};
                if (unlockInfo && unlockInfo.isUnlockedNow) {
                    let chapterUnlockType: string | undefined = undefined;
                    switch (unlockInfo.unlockNowMethod) {
                        case UnlockItemMethod.UnlockMethodWtu:
                            toastMessageFactory[ToastMessageTypes.CHAPTER_UNLOCKED_BY_WAITED_COUPON]();
                            chapterUnlockType = CHAPTER_UNLOCK_TYPES.WaitedCoupon;
                            break;
                        case UnlockItemMethod.UnlockMethodKarma:
                            toastMessageFactory[ToastMessageTypes.CHAPTER_UNLOCKED_BY_KARMA]({
                                karmaPrice: chapter?.karmaInfo?.karmaPrice?.value ?? '',
                            });
                            chapterUnlockType = CHAPTER_UNLOCK_TYPES.Karma;
                            break;
                        case UnlockItemMethod.UnlockMethodVoucher:
                            //TODO: Currently, only one voucher is needed to unlock any chapter. Remove this static value in future.
                            toastMessageFactory[ToastMessageTypes.CHAPTER_UNLOCKED_BY_VOUCHER]({
                                voucherAmount: 1,
                            });
                            chapterUnlockType = CHAPTER_UNLOCK_TYPES.Voucher;
                            break;
                    }
                    analyticsFactory(ChapterEvents.UnlockChapter)({
                        novel,
                        chapter,
                        published: !isAdvancedChapter,
                        type: chapterUnlockType,
                        on: CHAPTER_EVENT_SOURCES.Server,
                        autoUnlock: autoUnlockEnabled,
                    });
                }
            },
            retry: (count, error) => {
                if (error instanceof GrpcError && error.status === GrpcStatusCode.PERMISSION_DENIED) {
                    // If we don't have permission to access this chapter, fail without retry
                    return false;
                }

                return count < 2;
            },
            showModalOnError: true,
        }
    );

    const chapter = data?.item;
    const { isChapterLocked } = useChapterLockStatus({ novel, chapter });

    //If the chapter is a teaser and user has the page open before publish time,
    //it should automatically reload at the chapter publish time.
    //The following section simply sets a timer to refetch the chapter, 5 minutes after its publish time
    useEffect(() => {
        //To store the timeout function reference
        let revalidateHandle: number | NodeJS.Timeout | null = null;

        //Clear the timer when we refetch
        const revalidateChapter = () => {
            clearTimeout(revalidateHandle as any);

            loadChapter();
        };

        //Set the timer for the teaser chapter
        if (chapter?.isTeaser && chapter?.whenToPublish) {
            const publishDate = dayjs.unix(timestampToUnix(chapter.whenToPublish)!);
            const diff = publishDate.add(5, 'minute').diff(dayjs());

            if (diff > 0) {
                revalidateHandle = setTimeout(revalidateChapter, diff);
            }
        }

        //Clear the timeout if the page is unloaded for any reason
        return () => {
            if (revalidateHandle) {
                clearTimeout(revalidateHandle as any);
            }
        };
    }, [chapter?.isTeaser, chapter?.whenToPublish, loadChapter]);

    //If chapter was not found, show 404 error
    useEffect(() => {
        if (data && !data.item) {
            navigate('/404', {
                replace: true,
            });
        }
    }, [navigate, data]);

    useEffect(() => {
        if (novel && chapter && !isVipBenefitNovelPending && !isChampionSubscriptionPending) {
            const { isUnlockedChapter, isAdvancedChapter } = parseChapterStatus(chapter);
            const chapterStatus = determineChapterStatus({
                novel,
                chapter,
                isVipBenefitNovel,
                championedNovel: !!activeSubscription,
            });
            analyticsFactory(ChapterEvents.ViewChapterScreen)({
                novel,
                chapter,
                published: !isAdvancedChapter,
                type: (() => {
                    if (chapterStatus === 'unlockedByFree') return CHAPTER_UNLOCK_TYPES.Free;
                    if (chapterStatus === 'unlockedByOwned') return CHAPTER_UNLOCK_TYPES.Owned;
                    if (chapterStatus === 'unlockedByChampion') return CHAPTER_UNLOCK_TYPES.ChampionSubscription;
                    if (chapterStatus === 'unlockedByVip') return CHAPTER_UNLOCK_TYPES.VipSubscription;
                    return isUnlockedChapter ? CHAPTER_UNLOCK_TYPES.Unknown : undefined;
                })(),
                from,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        novel.slug,
        chapter?.slug,
        activeSubscription,
        isVipBenefitNovel,
        isVipBenefitNovelPending,
        isChampionSubscriptionPending,
    ]);

    useEffect(() => {
        if (!chapter) {
            return;
        }

        logGAEvent('chapter_viewed', {
            novel: novel.name,
            translator: novel.translatorName?.value || novel.translator?.userName || null,
            id: chapter.entityId,
            name: chapter.name,
            advanceChapter: !chapter.isTeaser && chapter.sponsorInfo?.advanceChapter ? 1 : 0,
            teaser: chapter.isTeaser.toString(),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chapter?.entityId]);

    const chapterContentLayoutStyle = useMemo(
        () => ({
            ...pick(chapterSettings?.viewerContrast[theme.name] || {}, ['color', 'backgroundColor']),
            transition: theme.transitions.create('all', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            ...(navOpened && {
                transition: theme.transitions.create('all', {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
                [theme.breakpoints.between(1200, 1679.95)]: {
                    marginLeft: '360px',
                    paddingLeft: '20px',
                    paddingRight: '20px',
                    maxWidth: 'initial',
                    '& > div': {
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        overflow: 'hidden',
                    },
                },
                [theme.breakpoints.up(1680)]: {
                    '& > div': {
                        paddingLeft: '0px',
                        paddingRight: '0px',
                        overflow: 'hidden',
                    },
                },
            }),
        }),
        [navOpened, chapterSettings, theme]
    );

    if (error) {
        return (
            <>
                <Helmet>
                    <title>Error!</title>
                </Helmet>
                <PageHeading center>Error loading chapter</PageHeading>
                <div css={ContentCenter}>
                    <ApiError error={error} />
                </div>
                <br />
                <div css={ContentCenter}>
                    <Button onClick={() => loadChapter()} color="secondary" size="large" variant="outlined">
                        Retry
                    </Button>
                </div>
            </>
        );
    }

    if (data.item?.novelInfo?.series) {
        const isDelisted = data.item.novelInfo.series.isDelisted;
        const canAccess = data.item.novelInfo.series.relatedUserInfo?.canAccess ?? true;

        if (isDelisted) {
            if (!canAccess) {
                return <NoLongerAvailable />;
            } else if (isChapterLocked) {
                return <NoLongerAvailableChapterLocked novel={novel} chapter={data.item} />;
            }
        }
    }

    return (
        <CurrentChapterContext.Provider value={chapter}>
            <div css={chapterContentLayoutStyle}>
                <Chapter novel={novel} chapter={chapter!} />
            </div>
            <NoSsr>
                <Suspense>
                    <ChapterNavigation
                        novel={novel ?? undefined}
                        chapter={chapter ?? undefined}
                        opened={navOpened}
                        onOpen={() => setNavOpened(true)}
                        onClose={() => setNavOpened(false)}
                    />
                </Suspense>
            </NoSsr>
        </CurrentChapterContext.Provider>
    );
}

export default function ChapterPageWrapper({ novel }: Props) {
    return (
        <Suspense
            fallback={
                <>
                    <ChapterContentPage>
                        <div css={{ '& >div': { width: '840px', marginRight: 'auto', marginLeft: 'auto' } }}>
                            <ChapterHeader novel={novel} />
                            <ChapterPlaceholder />
                            <ChapterLinks novel={novel} renderedFrom={CHAPTER_EVENT_SOURCES.ChapterViewBottom} />
                        </div>
                    </ChapterContentPage>
                    <NoSsr>
                        <ChapterNavigation novel={novel ?? undefined} />
                    </NoSsr>
                </>
            }
        >
            <ChapterPage novel={novel} />
        </Suspense>
    );
}
